import { useRef, useEffect, useState, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import { useFaceDetection } from '../../hooks/useFaceDetection';
import { Camera, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';

interface FaceCaptureProps {
	onCapture: (descriptor: number[]) => Promise<void> | void;
	onError?: (error: string) => void;
}

export default function FaceCapture({ onCapture, onError }: FaceCaptureProps) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const previewRunningRef = useRef(false);
	const [cameraReady, setCameraReady] = useState(false);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const [faceDetected, setFaceDetected] = useState(false);
	const [capturing, setCapturing] = useState(false);

	const { isModelLoaded, loadModels, detectFace } = useFaceDetection();

	// Load models on mount
	useEffect(() => {
		loadModels();
	}, [loadModels]);

	// Start camera when models are loaded
	useEffect(() => {
		if (!isModelLoaded) return;

		let cancelled = false;

		async function startCamera() {
			try {
				const stream = await navigator.mediaDevices.getUserMedia({
					video: { facingMode: 'user', width: 480, height: 360 },
				});
				if (cancelled) {
					stream.getTracks().forEach((t) => t.stop());
					return;
				}
				streamRef.current = stream;
				if (videoRef.current) {
					videoRef.current.srcObject = stream;
					await videoRef.current.play();
					setCameraReady(true);
				}
			} catch {
				setCameraError('Camera access denied. Please allow camera permissions.');
				onError?.('Camera access denied');
			}
		}

		startCamera();

		return () => {
			cancelled = true;
			streamRef.current?.getTracks().forEach((t) => t.stop());
			streamRef.current = null;
		};
	}, [isModelLoaded, onError]);

	// Periodic face detection for visual feedback only — uses faceapi directly
	// to avoid touching useFaceDetection's isDetecting state (which causes flicker)
	useEffect(() => {
		if (!cameraReady || !videoRef.current) return;

		let cancelled = false;

		async function runPreviewLoop() {
			while (!cancelled) {
				if (videoRef.current && !previewRunningRef.current) {
					previewRunningRef.current = true;
					try {
						const result = await faceapi
							.detectSingleFace(
								videoRef.current,
								new faceapi.TinyFaceDetectorOptions(),
							)
							.withFaceLandmarks();
						if (!cancelled) setFaceDetected(!!result);
					} catch {
						// ignore preview errors
					} finally {
						previewRunningRef.current = false;
					}
				}
				// Wait 600ms between checks
				await new Promise((r) => setTimeout(r, 600));
			}
		}

		runPreviewLoop();

		return () => {
			cancelled = true;
		};
	}, [cameraReady]);

	const handleCapture = useCallback(async () => {
		if (!videoRef.current || capturing) return;
		setCapturing(true);

		try {
			const descriptor = await detectFace(videoRef.current);
			if (descriptor) {
				await onCapture(descriptor);
			} else {
				onError?.('No face detected. Please position your face in the frame.');
			}
		} finally {
			setCapturing(false);
		}
	}, [detectFace, onCapture, onError, capturing]);

	if (!isModelLoaded) {
		return (
			<div className='flex flex-col items-center justify-center py-12 gap-3'>
				<Loader2 size={32} className='animate-spin text-brand-orange' />
				<p className='text-body-sm text-text-secondary'>
					Loading face detection models...
				</p>
			</div>
		);
	}

	if (cameraError) {
		return (
			<div className='flex flex-col items-center justify-center py-12 gap-3'>
				<AlertCircle size={32} className='text-status-error' />
				<p className='text-body-sm text-status-error text-center'>
					{cameraError}
				</p>
			</div>
		);
	}

	return (
		<div className='flex flex-col items-center gap-4'>
			<div className='relative w-full max-w-[320px] aspect-[4/3] rounded-card overflow-hidden bg-black'>
				<video
					ref={videoRef}
					autoPlay
					playsInline
					muted
					className='w-full h-full object-cover'
					style={{ transform: 'scaleX(-1)' }}
				/>
				{/* Face detection overlay */}
				<div
					className={`absolute inset-0 border-4 rounded-card transition-colors duration-300 ${
						faceDetected ? 'border-status-success' : 'border-white/30'
					}`}
				/>
			</div>

			{/* Status text */}
			<div className='flex items-center gap-2'>
				{faceDetected ? (
					<>
						<CheckCircle2 size={16} className='text-status-success' />
						<span className='text-body-sm text-status-success'>
							Face detected
						</span>
					</>
				) : (
					<>
						<Camera size={16} className='text-text-muted' />
						<span className='text-body-sm text-text-muted'>
							Position your face in the frame
						</span>
					</>
				)}
			</div>

			<button
				onClick={handleCapture}
				disabled={!faceDetected || capturing}
				className='w-full max-w-[320px] py-2.5 rounded-input font-medium text-body-sm transition-colors
					bg-brand-orange text-text-inverse hover:bg-brand-orange/90
					disabled:opacity-50 disabled:cursor-not-allowed'>
				{capturing ? (
					<span className='flex items-center justify-center gap-2'>
						<Loader2 size={16} className='animate-spin' />
						Capturing...
					</span>
				) : (
					'Capture Face'
				)}
			</button>
		</div>
	);
}
